<script lang="ts" setup>
import type { Component } from 'vue'

import { ref, computed, markRaw, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAuthStore } from '@/stores/auth-store'
import { LicenseType } from '@/types/company'

import Breadcrumb from '@/components/layout/Breadcrumb.vue'
import CrumbsAndActions from '@/components/layout/CrumbsAndActions.vue'
import DriverReportPreferences from '@/components/settings/DriverReportPreferences.vue'
import DeliveryManagementPreferences from '@/components/settings/DeliveryManagementPreferences.vue'
import DamageReportPreferences from '@/components/settings/DamageReportPreferences.vue'
import PodPreferences from '@/components/settings/PodPreferences.vue'
import PackagingPreferences from '@/components/settings/PackagingPreferences.vue'
import MyPanel from '@/components/my-components/MyPanel.vue'

interface LicenseOption {
    type: LicenseType
    label: string
    component: Component
}

const { t } = useI18n()
const authStore = useAuthStore()
const selectedLicense = ref<LicenseOption | null>(null)

const licenses = computed<LicenseOption[]>(() =>
    [
        {
            type: LicenseType.PODModule,
            label: t('pod'),
            component: markRaw(PodPreferences),
        },
        {
            type: LicenseType.PackagingModule,
            label: t('packaging'),
            component: markRaw(PackagingPreferences),
        },
        {
            type: LicenseType.DriverReportModule,
            label: t('driverReports'),
            component: markRaw(DriverReportPreferences),
        },
        {
            type: LicenseType.DamageReportModule,
            label: t('damageReports'),
            component: markRaw(DamageReportPreferences),
        },
        {
            type: LicenseType.DeliveryManagementModule,
            label: t('deliveryManagement'),
            component: markRaw(DeliveryManagementPreferences),
        },
    ].filter((license) => authStore.hasLicense(license.type)),
)

function selectLicense(license: LicenseOption) {
    selectedLicense.value = license
}

onMounted(() => {
    selectedLicense.value = licenses.value[0] ?? null
})
</script>

<template>
    <CrumbsAndActions>
        <Breadcrumb :to="{ name: 'settings' }" v-text="t('settings')" />
        <Breadcrumb current v-text="t('licenses')" />
    </CrumbsAndActions>

    <div class="mx-auto w-full max-w-6xl">
        <h1 class="text-xl font-bold" v-text="t('licenses')" />
        <div class="flex items-start gap-6 mt-5">
            <!-- Sidebar -->
            <aside class="w-full max-w-xs flex-shrink-0 space-y-4">
                <div
                    v-for="license in licenses"
                    :key="license.type"
                    class="w-full cursor-pointer rounded-lg p-5 transition-colors"
                    :class="{
                        'bg-stone-200 dark:bg-dark-500': selectedLicense?.type !== license.type,
                        'bg-primary-500 text-white': selectedLicense?.type === license.type,
                    }"
                    @click="selectLicense(license)"
                    v-text="license.label"
                />
            </aside>
            <MyPanel panel-class="mx-auto w-full max-w-5xl space-y-4 p-6" shadow>
                <component
                    :is="selectedLicense?.component"
                    v-if="selectedLicense"
                    :key="selectedLicense.type"
                />
                <div v-else class="text-center text-stone-500">
                    {{ t('selectALicense') }}
                </div>
            </MyPanel>
        </div>
    </div>
</template>
